import { useEffect, useState } from "react";
import { MdModeEdit } from "react-icons/md";
import {
  useAddAdminMutation,
  useGetLookupValuesMutation,
  Lookup,
} from "../../api/api";
import { Plus, X } from "lucide-react";
import SkeletonAddEditCategoryDialog from "../../skeletonLoading/SkeletonAddEditCategoryDialog";
import { LOOKUPS, MIN_PASSWORD_LENGTH, REGEXES } from "../../lib/constants";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { UseFormRegister, FieldErrors } from "react-hook-form";
import { useToast } from "../../@/components/ui/use-toast";
import SortupDialog from "../../components/common/SortupDialog";
import GenericDropdown from "../../components/GenericDropDown";
type AddEditAdminDialogProps = {
  data?: any;
  isEditOnly: boolean;
  fetchData: () => void;
  setPageNumber: (pageNumber: number) => void;
  setSearchTerm: (searchTerm: string) => void;
  adminData?: AdminDialog;
  setValue?: any;
};

interface AdminDialog {
  register?: UseFormRegister<AddEditAdminDialogProps>;
  errors?: FieldErrors<AddEditAdminDialogProps>;
  setValue?: any;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  statusId: number;
}

export function AddEditAdminDialog({
  data,
  isEditOnly,
  fetchData,
  setSearchTerm,
  setPageNumber,
  adminData,
}: AddEditAdminDialogProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [adminValues, setAdminValues] = useState<AdminDialog>({
    email: "",
    firstName: "",
    lastName: "",
    password: "",
    statusId: 0,
  });
  const { toast } = useToast();

  const [AddAdmin, { isLoading: isAddAdminLoading }] = useAddAdminMutation();
  const [getLookupValues, { isLoading: statusesLoading }] =
    useGetLookupValuesMutation();
  const [statuses, setStatuses] = useState<Lookup[]>([]);
  const fetchStatuses = async () => {
    try {
      const result = await getLookupValues({
        lookupIdDto: { lookupId: LOOKUPS.UserStatus },
      });
      if (result.data) {
        setStatuses(result.data.data);
        if (isEditOnly && data) {
          setAdminValues((prevValues) => ({
            ...prevValues,
            statusId: data.statusId,
          }));
        } else if (!isEditOnly) {
          setAdminValues((prevValues) => ({
            ...prevValues,
            statusId: result.data.data.length > 0 ? result.data.data[0].id : 0,
          }));
        } else {
          const defaultStatus = result.data.data[0]?.id || 0;
          setAdminValues((prev) => ({
            ...prev,
            statusId: defaultStatus,
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching statuses:", error);
    }
  };
  useEffect(() => {
    if (isDialogOpen) {
      fetchStatuses();
    }
  }, [isDialogOpen]);
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: keyof AdminDialog
  ) => {
    const value = e.target.value;
    setAdminValues({ ...adminValues, [field]: value });
  };

  const handleDialogOpen = () => {
    setIsDialogOpen(true);
    if (isEditOnly && data) {
      const nameParts = data.userName.split(" ");
      setAdminValues({
        email: data.email,
        firstName: nameParts[0] || "",
        lastName: nameParts[1] || "",
        password: "",
        statusId: data.statusId,
      });
    } else {
      setAdminValues({
        email: "",
        firstName: "",
        lastName: "",
        password: "",
        statusId: statuses[0]?.id || 0,
      });
    }
  };

  const handleAdminAction = async () => {
    if (isAddAdminLoading) return;

    let adminPayload = {
      firstName: adminValues.firstName,
      lastName: adminValues.lastName,
      email: adminValues.email,
      password: adminValues.password === "" ? undefined : adminValues.password,
      statusId: adminValues.statusId,
      id: undefined,
    };

    try {
      if (isEditOnly && data) {
        adminPayload.id = data.id;
      }
      console.log("adminPayload", adminPayload);
      const response: any = await AddAdmin({
        adminSignupDto: adminPayload,
      });

      if (response && response?.data?.message.includes("success")) {
        setIsDialogOpen(false);
        setPageNumber(1);
        fetchData();
        toast({
          title: "Success",
          description: response?.data?.message,
        });
      } else {
        toast({
          title: "Error",
          description: response?.error?.data?.message,
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
      });
    }
  };

  const isFormValid =
    adminValues.firstName === "" ||
    adminValues.lastName === "" ||
    adminValues.email === "" ||
    !REGEXES.EMAIL.test(adminValues.email) ||
    (isEditOnly
      ? false // If editing, no need to validate password (it can remain empty)
      : adminValues.password === "" ||
        adminValues.password.length < MIN_PASSWORD_LENGTH ||
        !REGEXES.PASSWORD.test(adminValues.password));

  return (
    <>
      <div className="flex items-center space-x-2">
        {isEditOnly ? (
          <div onClick={handleDialogOpen}>
            <MdModeEdit
              size={16}
              className="cursor-pointer text-[#72767E] hover:text-black"
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={handleDialogOpen}
            className="flex text-sm font-normal items-center justify-center bg-customGreenColor text-white px-4 py-2 gap-1 rounded-lg w-full"
          >
            <Plus className="w-4 h-4" />
            Add Admin
          </button>
        )}
      </div>
      <SortupDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        header={isEditOnly ? "Edit Admin" : "Add Admin"}
        bodyClassName="w-full min-w-[37vw]"
        footerButtons={[
          {
            name: isEditOnly ? "Update" : "Add",
            onClick: isEditOnly ? handleAdminAction : handleAdminAction,
            className: isFormValid
              ? "opacity-50 cursor-not-allowed text-white"
              : " text-white ",
            disabled: isFormValid,
          },
          {
            name: "Cancel",
            className: "bg-white text-gray-900 border",
            disabled: isAddAdminLoading,
            onClick: () => setIsDialogOpen(false),
          },
        ]}
      >
        {isAddAdminLoading ? (
          <SkeletonAddEditCategoryDialog rows={1} />
        ) : (
          <>
            <div className="mt-4 px-2">
              <label
                htmlFor="firstName"
                className="block text-sm font-medium text-gray-700"
              >
                First Name
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                id="firstName"
                value={adminValues.firstName}
                onChange={(e) => handleInputChange(e, "firstName")}
                className="focus:outline-customGreenColor mt-1 p-2 border border-gray-300 rounded-md w-full"
              />
            </div>
            <div className="mt-4 px-2">
              <label
                htmlFor="lastName"
                className="block text-sm font-medium text-gray-700"
              >
                Last Name
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="text"
                id="lastName"
                value={adminValues.lastName}
                onChange={(e) => handleInputChange(e, "lastName")}
                className="focus:outline-customGreenColor mt-1 p-2 border border-gray-300 rounded-md w-full"
              />
            </div>
            <div className="mt-4 px-2">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email Address
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="email"
                id="email"
                disabled={isEditOnly}
                value={adminValues.email}
                onChange={(e) => handleInputChange(e, "email")}
                className={`focus:outline-customGreenColor mt-1 p-2 border ${
                  !adminValues.email || REGEXES.EMAIL.test(adminValues.email)
                    ? "border-gray-300"
                    : "border-red-500"
                } rounded-md w-full`}
              />
              {adminValues.email && !REGEXES.EMAIL.test(adminValues.email) && (
                <div className="mt-1">
                  <p className="text-sm text-red-500">
                    • Please enter a valid email address
                  </p>
                </div>
              )}
            </div>
            {!isEditOnly && (
              <div className="mt-4 px-2">
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700"
                >
                  Password
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={adminValues.password}
                    onChange={(e) => handleInputChange(e, "password")}
                    placeholder="Enter password"
                    className={`focus:outline-customGreenColor mt-1 p-2 border ${
                      !adminValues.password ||
                      (adminValues.password.length >= MIN_PASSWORD_LENGTH &&
                        REGEXES.PASSWORD.test(adminValues.password))
                        ? "border-gray-300"
                        : "border-red-500"
                    } rounded-md w-full pr-10`}
                  />
                  <div
                    className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <AiOutlineEyeInvisible className="xxl:text-2xl text-xl text-customGrayBase" />
                    ) : (
                      <AiOutlineEye className="xxl:text-2xl text-xl text-customGrayBase" />
                    )}
                  </div>
                </div>
                {adminValues.password &&
                  (!REGEXES.PASSWORD.test(adminValues.password) ||
                    adminValues.password.length < MIN_PASSWORD_LENGTH) && (
                    <div className="mt-1 space-y-1">
                      <p
                        className={`text-sm ${
                          adminValues.password.length >= MIN_PASSWORD_LENGTH
                            ? "text-green-500"
                            : "text-red-500"
                        }`}
                      >
                        • Minimum 8 characters
                      </p>
                      <p
                        className={`text-sm ${
                          REGEXES.PASSWORD.test(adminValues.password)
                            ? "text-green-500"
                            : "text-red-500"
                        }`}
                      >
                        • Must contain at least one uppercase and one lowercase
                        letter
                      </p>
                    </div>
                  )}
              </div>
            )}
            <div className="mt-4 px-2">
              <label
                htmlFor="status"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Status
                <span className="text-red-500 ml-1">*</span>
              </label>
              <GenericDropdown
                labelValue={
                  statuses.find((status) => status.id === adminValues.statusId)
                    ?.name || ""
                }
                onChange={(value: string) => {
                  setAdminValues({
                    ...adminValues,
                    statusId:
                      statuses.find((status) => status.name === value)?.id || 0,
                  });
                }}
                options={statuses.map((status) => ({
                  value: status.name,
                  label: status.name,
                }))}
                placeholder="Select a status"
                error={false}
                className="w-full justify-between"
                dropdownMenuContentWidth="w-[35vw]"
              />
            </div>
          </>
        )}
      </SortupDialog>
    </>
  );
}
